import type { OnboardingQueueState } from '@/lib/api/onboarding';

export function shouldOpenWalletGate(input: {
    locallyCompleted: boolean;
    featureEnabled: boolean;
    hasCustodialWallet: boolean;
}): boolean {
    return (
        !input.locallyCompleted ||
        (input.featureEnabled && !input.hasCustodialWallet)
    );
}

/**
 * Whether onboarding may evaluate/auto-open for this session.
 *
 * Real mode is the established trigger. Wallet-first sign-in adds the second
 * arm: an app-token identity with no Snappy account yet is exactly the user the
 * flow exists for, and they cannot reach real mode until it has run.
 */
export function shouldEvaluateOnboarding(input: {
    isRealMode: boolean;
    isAuthenticated: boolean;
    hasAddress: boolean;
    hasCustodialWallet: boolean;
}): boolean {
    if (!input.hasAddress) return false;
    return (
        input.isRealMode || (input.isAuthenticated && !input.hasCustodialWallet)
    );
}

/**
 * Who signs for their own wallets.
 *
 * A social identity normally has its Snappy account created for it by the
 * admission queue, and must never be dropped on a step it cannot act on. The
 * exception is a social login with no account behind it yet: the run that
 * creates one is authorized by a connected owner EOA, which signs the Cronos leg
 * itself, so that cohort takes the wallet-first path even though a social
 * session exists.
 */
export function needsSelfProvisioning(input: {
    isSocialAuthenticated: boolean;
    hasCustodialWallet: boolean;
    /** Onboarding is running to associate a social login with a new account. */
    hasSocialAssociationIntent: boolean;
}): boolean {
    if (input.hasCustodialWallet) return false;
    return !input.isSocialAuthenticated || input.hasSocialAssociationIntent;
}

/**
 * Where a returning user picks up.
 *
 * Every input is a durable fact that survives closing the app: the local terms
 * record, the account reads, the admission queue row and the on-chain Cronos
 * authorization wallet. Nothing here depends on in-memory session state, which
 * is what lets a reload resume on the same step rather than restarting at the
 * queue and relying on a follow-up effect to bounce the user onwards.
 */
export function onboardingEntryStep(input: {
    termsAccepted: boolean;
    hasCustodialWallet: boolean;
    isSocialAuthenticated: boolean;
    isWalletAuthenticated: boolean;
    /**
     * The social login being associated with the account this run creates. Its
     * owner is an EOA, so the wallet step is theirs to clear.
     */
    hasSocialAssociationIntent?: boolean;
    /** Admission queue state, when it has been read. */
    queueState?: OnboardingQueueState | null;
    /**
     * `wallets(owner)` on the Cronos manager is non-zero. Null when the read
     * failed or was not attempted, which falls back to the queue-only answer.
     */
    authorizationWalletExists?: boolean | null;
}): 'welcome' | 'wallet' | 'voucher' | 'provision' | 'funding' {
    if (!input.termsAccepted) return 'welcome';
    if (input.hasCustodialWallet) return 'funding';
    const ownerSigns = needsSelfProvisioning({
        isSocialAuthenticated: input.isSocialAuthenticated,
        hasCustodialWallet: input.hasCustodialWallet,
        hasSocialAssociationIntent: !!input.hasSocialAssociationIntent,
    });
    // an authenticated identity already cleared the wallet step, whichever way
    // it was obtained; the admission queue is the next thing that gates them.
    // The association cohort is the exception: their account is created by an
    // owner EOA, so a social session alone does not clear it for them.
    if (ownerSigns && !input.isWalletAuthenticated) return 'wallet';
    // Social users never sign for their own wallets, so the queue step is where
    // their whole admission, including its provisioning, is reported.
    if (!ownerSigns) return 'voucher';

    const queueState = input.queueState ?? null;
    // The lane is theirs and the signing is theirs to do: back to the ceremony,
    // whether it never ran, failed detached, or is still running.
    if (queueState === 'admitted' || queueState === 'provisioning') {
        return 'provision';
    }
    // No queue to route through (gate off, or its read never landed), so the
    // on-chain half-finished ceremony is the only fact left, and it says the
    // user was partway through signing for their own wallets.
    if (
        (queueState === null || queueState === 'disabled') &&
        input.authorizationWalletExists
    ) {
        return 'provision';
    }
    // queued, ready, missed, notRedeemed and cancelled all have their own UI on
    // the admission step, which is also where a lost lane is won back.
    return 'voucher';
}

export function stepAfterWalletSetup(input: {
    requiresAdmission: boolean;
    needsProvisioning: boolean;
}): 'voucher' | 'provision' | 'funding' {
    if (input.requiresAdmission) return 'voucher';
    return input.needsProvisioning ? 'provision' : 'funding';
}

/**
 * Social users get their wallet created for them by the admission queue, so
 * they leave the voucher step for funding. Wallet-only users have to sign the
 * two provisioning legs themselves first.
 */
export function stepAfterVoucher(input: {
    needsProvisioning: boolean;
}): 'provision' | 'funding' {
    return input.needsProvisioning ? 'provision' : 'funding';
}

/**
 * Whether a social login that just came back unbound belongs to the onboarding
 * dialog rather than the regular binding dialog.
 *
 * A first-time user who accepted the terms and has not finished onboarding is
 * still inside the wizard, so the link is one more step of it; everyone else
 * gets the regular dialog. Both dialogs evaluate this from the same stored
 * record, so exactly one of them ever renders.
 */
export function claimsSocialLink(input: {
    termsAccepted: boolean;
    locallyCompleted: boolean;
}): boolean {
    return input.termsAccepted && !input.locallyCompleted;
}
